/**
 * Admin Authentication Context
 *
 * Provides admin role checking for the internal admin console.
 * Checks admin_users collection for staff with admin privileges.
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { doc, getDoc, onSnapshot, Timestamp, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { AdminRole, AdminUser } from '@/integrations/firebase/types';

interface AdminAuthContextType {
  // Admin user data
  adminUser: AdminUser | null;
  adminRole: AdminRole | null;

  // Loading state
  isLoading: boolean;

  // Permission checks
  isAdminUser: boolean;
  isSuperadmin: boolean;
  isAdmin: boolean;
  isSupport: boolean;
  isDev: boolean;

  // Permission helpers
  canManageOrgs: boolean;
  canManageUsers: boolean;
  canManagePlans: boolean;
  canImpersonate: boolean;
  canViewAuditLogs: boolean;
  canRunChecks: boolean;
  canDownloadDebugBundles: boolean;

  // Refresh admin data
  refreshAdminUser: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch admin user data
  const fetchAdminUser = async () => {
    if (!user?.uid) {
      setAdminUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const adminDocRef = doc(db, 'admin_users', user.uid);
      const adminDoc = await getDoc(adminDocRef);

      if (adminDoc.exists()) {
        const data = adminDoc.data();

        // Only set if active
        if (data.isActive !== false) {
          setAdminUser({
            uid: user.uid,
            email: data.email || user.email || '',
            displayName: data.displayName,
            adminRole: data.adminRole,
            createdAt: data.createdAt,
            lastLoginAt: data.lastLoginAt,
            isActive: data.isActive !== false,
          });

          // Update last login
          await updateDoc(adminDocRef, {
            lastLoginAt: serverTimestamp(),
          });
        } else {
          setAdminUser(null);
        }
      } else {
        setAdminUser(null);
      }
    } catch (error) {
      console.error('Error fetching admin user:', error);
      setAdminUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Subscribe to admin user changes
  useEffect(() => {
    if (!user?.uid) {
      setAdminUser(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const adminDocRef = doc(db, 'admin_users', user.uid);
    const unsubscribe = onSnapshot(
      adminDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.isActive !== false) {
            setAdminUser({
              uid: user.uid,
              email: data.email || user.email || '',
              displayName: data.displayName,
              adminRole: data.adminRole,
              createdAt: data.createdAt,
              lastLoginAt: data.lastLoginAt,
              isActive: data.isActive !== false,
            });
          } else {
            setAdminUser(null);
          }
        } else {
          setAdminUser(null);
        }
        setIsLoading(false);
      },
      (error) => {
        console.error('Error listening to admin user:', error);
        setAdminUser(null);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid, user?.email]);

  // Derive role checks
  const adminRole = adminUser?.adminRole || null;
  const isAdminUser = !!adminUser && adminUser.isActive;
  const isSuperadmin = adminRole === 'superadmin';
  const isAdmin = adminRole === 'admin' || isSuperadmin;
  const isSupport = adminRole === 'support' || isAdmin;
  const isDev = adminRole === 'dev' || isSuperadmin;

  // Permission helpers based on roles
  const canManageOrgs = isAdmin; // superadmin, admin
  const canManageUsers = isAdmin; // superadmin, admin
  const canManagePlans = isSuperadmin; // superadmin only
  const canImpersonate = isAdmin; // superadmin, admin
  const canViewAuditLogs = isAdminUser; // all admin roles
  const canRunChecks = isAdmin; // superadmin, admin (support needs elevation)
  const canDownloadDebugBundles = isDev || isSupport; // dev, support, superadmin

  const value: AdminAuthContextType = {
    adminUser,
    adminRole,
    isLoading,
    isAdminUser,
    isSuperadmin,
    isAdmin,
    isSupport,
    isDev,
    canManageOrgs,
    canManageUsers,
    canManagePlans,
    canImpersonate,
    canViewAuditLogs,
    canRunChecks,
    canDownloadDebugBundles,
    refreshAdminUser: fetchAdminUser,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}

// Helper hook to require specific admin role
export function useRequireAdminRole(requiredRole: AdminRole | AdminRole[]): boolean {
  const { adminRole, isLoading } = useAdminAuth();

  if (isLoading) return false;
  if (!adminRole) return false;

  const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

  // Superadmin has access to everything
  if (adminRole === 'superadmin') return true;

  return roles.includes(adminRole);
}
